import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Dumbbell, Award, Check } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { hasProfileQueryKey } from '@/features/profile/hooks/useHasProfile';
import { isProfileComplete } from '@/features/profile/isProfileComplete';
import { markOnboardingPending } from '@/features/onboarding/pendingOnboarding';
import { useI18n } from '@/contexts/I18nContext';
import { useToast } from '@/shared/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Input } from '@/components/ui/input';
import { DateInput } from '@/components/ui/DateInput';
import { Button } from '@/components/ui/button';
import { AgeBlockedScreen } from '@/components/auth/AgeBlockedScreen';
import { normalizeUsername, validateUsername } from '@/utils/usernameValidation';
import { validateDateOfBirth, meetsMinimumAge, isAgeBlocked, setAgeBlocked, todayYmd } from '@/utils/ageGate';
import { getAttribution } from '@/utils/attribution';
import { pushEvent as gtmPushEvent } from '@/integrations/analytics/gtm';
import { GTM_SIGN_UP } from '@/integrations/analytics/events';
import { cn } from '@/lib/utils';
import logo from '@/assets/images/logo/logo.svg';

type UsernameStatus = 'idle' | 'checking' | 'available' | 'unavailable' | 'invalid';

/**
 * Google users authenticate without a profile — they land here to provide the
 * fields the OAuth flow can't give us (username, role, date of birth), which
 * creates the profile. Belt/degrees/academy are then collected by the shared
 * onboarding, exactly like the email flow.
 */
const CompleteProfile = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { t, currentLanguage } = useI18n();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [username, setUsername] = useState('');
  const [usernameStatus, setUsernameStatus] = useState<UsernameStatus>('idle');
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [isInstructor, setIsInstructor] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [dobTouched, setDobTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [blocked, setBlocked] = useState<boolean>(() => isAgeBlocked());

  const maxDob = useMemo(() => todayYmd(), []);

  const checkUsername = useCallback(async (value: string) => {
    const normalized = normalizeUsername(value);
    if (!validateUsername(normalized)) {
      setUsernameStatus('invalid');
      return;
    }
    setUsernameStatus('checking');
    const { data, error } = await supabase.rpc('check_username_available', { input_username: normalized });
    if (error) { setUsernameStatus('invalid'); return; }
    setUsernameStatus(data ? 'available' : 'unavailable');
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (username && usernameTouched) checkUsername(username);
    }, 500);
    return () => clearTimeout(timer);
  }, [username, usernameTouched, checkUsername]);

  const usernameValidationState = !usernameTouched
    ? undefined
    : usernameStatus === 'available'
      ? 'success' as const
      : usernameStatus === 'checking'
        ? 'checking' as const
        : usernameStatus === 'unavailable'
          ? 'unavailable' as const
          : usernameStatus === 'invalid'
            ? 'error' as const
            : undefined;

  const usernameMessage = !usernameTouched ? '' :
    usernameStatus === 'available' ? `@${username} ${t('auth.step_two.username_available')}` :
    usernameStatus === 'unavailable' ? t('auth.step_two.username_unavailable') :
    usernameStatus === 'checking' ? t('auth.step_two.checking_username') :
    usernameStatus === 'invalid' ? t('auth.step_two.username_invalid') : '';

  const dobError = dobTouched && validateDateOfBirth(dateOfBirth) !== null;

  const isValid =
    usernameStatus === 'available' &&
    validateDateOfBirth(dateOfBirth) === null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUsernameTouched(true);
    setDobTouched(true);

    if (!user) return;
    if (usernameStatus !== 'available') return;
    if (validateDateOfBirth(dateOfBirth) !== null) return;

    // Age gate — the account already exists (Google), so block after auth.
    if (!meetsMinimumAge(dateOfBirth)) {
      setAgeBlocked();
      setBlocked(true);
      return;
    }

    setLoading(true);
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      // Google does give us the display name — carry it over so the profile
      // isn't created nameless and the onboarding opens with it pre-filled.
      const metadata = (user.user_metadata ?? {}) as Record<string, unknown>;
      const googleName =
        typeof metadata.full_name === 'string'
          ? metadata.full_name
          : typeof metadata.name === 'string'
            ? metadata.name
            : '';

      const { error } = await supabase.rpc('create_profile', {
        user_id: user.id,
        user_metadata: {
          username: normalizeUsername(username),
          isInstructor,
          birthday: dateOfBirth,
          preferred_language: currentLanguage,
          timezone,
          beltRank: 'white',
          fullName: googleName,
          // Captured at landing and read back here: this is the Google door,
          // and the sign-in redirect returns to the same origin, so the
          // localStorage written before the redirect is still ours.
          ...getAttribution(),
        },
      });

      if (error) {
        toast({ title: t('auth.error'), description: t('auth.complete_signup.error'), variant: 'destructive' });
        return;
      }

      const handoff = { username: normalizeUsername(username), isInstructor };

      // Persist the hand-off BEFORE navigating. Onboarding also reads it from
      // the router state, but that state doesn't survive a reload or a guard
      // redirect — and a user who loses it lands on /home with a half-finished
      // profile, which is exactly what happened in production.
      markOnboardingPending(handoff);

      // Seed the profile-guard cache so the route guards don't bounce us back.
      // Derived from the same rule the guards apply rather than hardcoded to
      // `true`: if the profile we just created still wouldn't satisfy them, the
      // right outcome is to find that out here, not to assert otherwise and have
      // the user bounce on the next navigation.
      queryClient.setQueryData(
        hasProfileQueryKey(user.id),
        isProfileComplete({
          hasRow: true,
          username: handoff.username,
          provider: user.app_metadata?.provider,
        }),
      );
      gtmPushEvent(GTM_SIGN_UP);
      navigate('/onboarding', { replace: true, state: handoff });
    } catch {
      toast({ title: t('auth.error'), description: t('auth.complete_signup.error'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleBackToStart = async () => {
    await signOut();
    navigate('/', { replace: true });
  };

  if (blocked) {
    return (
      <div className="min-h-[100dvh] bg-sossiety-dark px-6">
        <AgeBlockedScreen onBackToStart={handleBackToStart} />
      </div>
    );
  }

  return (
    <div className="flex min-h-[100dvh] flex-col bg-sossiety-dark">
      <header
        className="flex-shrink-0 border-b border-white/10 px-6"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
      >
        <div className="mx-auto flex h-14 max-w-[440px] items-center">
          <img src={logo} alt="sOSSiety" className="h-7 w-auto" />
        </div>
      </header>

      <main className="flex-1 px-6 pb-8">
        <div className="mx-auto max-w-[440px] pt-8">
          <h1 className="text-[28px] font-extrabold leading-tight tracking-tight text-white">
            {t('auth.complete_signup.title')}
          </h1>
          <p className="mt-1 text-[15px] text-white/60">{t('auth.complete_signup.subtitle')}</p>

          <form onSubmit={handleSubmit} noValidate className="mt-8 space-y-5">
            {/* Username */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-white/70">{t('auth.step_two.choose_username')}</label>
              <Input
                type="text"
                autoComplete="off"
                placeholder="@seuusername"
                value={username}
                onChange={(e) => {
                  setUsername(normalizeUsername(e.target.value));
                  setUsernameTouched(true);
                  setUsernameStatus('idle');
                }}
                onBlur={() => { setUsernameTouched(true); if (username) checkUsername(username); }}
                maxLength={32}
                showFloatingLabel={false}
                validationState={usernameValidationState}
                validationMessage={usernameTouched ? usernameMessage : undefined}
              />
            </div>

            {/* Role */}
            <div className="space-y-2">
              <label className="text-xs font-medium text-white/70">{t('auth.step_two.are_you')}</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setIsInstructor(false)}
                  className={cn(
                    'relative flex flex-col items-center gap-2 rounded-xl border px-4 py-4 transition-all',
                    !isInstructor ? 'border-sossiety-red bg-sossiety-red-surface' : 'border-[rgba(255,255,255,0.1)] bg-[#231a1a]'
                  )}
                >
                  <Dumbbell className="h-6 w-6 text-white" />
                  <span className="text-sm font-medium text-white">{t('auth.step_two.practitioner')}</span>
                  {!isInstructor && (
                    <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-sossiety-red">
                      <Check className="h-3 w-3 text-white" />
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setIsInstructor(true)}
                  className={cn(
                    'relative flex flex-col items-center gap-2 rounded-xl border px-4 py-4 transition-all',
                    isInstructor ? 'border-sossiety-red bg-sossiety-red-surface' : 'border-[rgba(255,255,255,0.1)] bg-[#231a1a]'
                  )}
                >
                  <Award className="h-6 w-6 text-white" />
                  <span className="text-sm font-medium text-white">{t('auth.step_two.instructor')}</span>
                  {isInstructor && (
                    <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-sossiety-red">
                      <Check className="h-3 w-3 text-white" />
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Date of birth */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-white/70" htmlFor="complete-dob">
                {t('auth.step_two.birthday_label')}
              </label>
              <DateInput
                id="complete-dob"
                value={dateOfBirth}
                onChange={(v) => { setDateOfBirth(v); }}
                onBlur={() => setDobTouched(true)}
                max={maxDob}
                ariaInvalid={dobError}
                dataSignupField="dateOfBirth"
                inputClassName="bg-sossiety-input text-white border-[rgba(255,255,255,0.1)] placeholder:text-white/40 focus:ring-sossiety-red/40 !rounded-xl"
              />
              {dobError && <p className="text-xs text-red-300">{t('auth.step_two.birthday_required')}</p>}
            </div>

            <Button type="submit" variant="primary" size="lg" disabled={loading || !isValid} className="w-full">
              {loading ? t('auth.complete_signup.creating') : t('auth.complete_signup.create')}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default CompleteProfile;
